import type { InboxChannel, InboxMessage } from '../types/inbox.js';

// DM MESSAGING WINDOW -- the one definition, shared by the server (which tag to
// send, what to say when Meta refuses) and the composer (warn, block, countdown).
//
// Meta's rule: a reply is allowed within 24 HOURS of the customer's last
// message. The HUMAN_AGENT message tag extends that to 7 DAYS, but only for a
// reply a person actually wrote AND only for an app Meta has approved for the
// tag. See _architect/findings/2026-09-24-ig-dm-window-reports-as-502.md.
//
// ⚠️⚠️ WHAT IS TRUE NOW (2026-10-07): THE TAG IS NOT APPROVED, SO WE NEVER SEND
// IT. The 2026-09-24 build tagged Instagram replies and this header claimed the
// tag was "measured live and approved". It was not. Prod logged three refusals
// on 2026-10-06 (01:43, 01:44, 05:02 UTC), all statusCode 403:
//   "To use 'Human Agent', your use of this endpoint must be reviewed and
//    approved by Facebook."
// Zernio confirmed the same day that their Meta app's Human Agent approval is
// still under review with Meta and has never been granted. Until it is,
// Instagram replies get the standard 24h window. One exception Zernio
// reported: an UNANSWERED MESSAGE REQUEST can be answered later (a 27-day-old
// one went through from Zernio's UI). We cannot tell those threads apart, so
// past its window the Instagram composer WARNS but never blocks -- and that
// holds whether or not the tag is approved, because the message-request
// exception applies past ANY window (24h now, 7 days once approved).
//
// HUMAN_AGENT_APPROVED is the one switch. Flip it to true when Zernio confirms
// Meta's approval and tagging plus the 7-day window come back with no other
// change -- the provenance gate below is untouched by the flag.
//
// ⚠️⚠️ THE TAG IS A COMPLIANCE CLAIM, NOT A FLAG. It tells Meta a human wrote
// this. So it is gated on a REQUIRED provenance (`DmSendOrigin`), never a
// default: a future automated caller (auto-reply, scheduled send, an AI draft
// sent without someone pressing Send) must declare itself 'automated' at the
// call site and cannot inherit the tag.
//
// ⚠️ INSTAGRAM ONLY, even once approved. On Facebook Messenger HUMAN_AGENT is a
// separate app permission; a tagged send there could be refused even INSIDE
// 24h, breaking Facebook DMs that work today. Facebook stays untagged on its
// 24h window until a probe confirms the tag is accepted.

/**
 * ⚠️ FALSE until Meta approves Zernio's app for HUMAN_AGENT. Evidence: the
 * 403 above (prod, 2026-10-06) and Zernio's confirmation the same day that the
 * approval is still under review. While false NO DM carries messagingType /
 * messageTag, and Instagram's window is 24h.
 */
export const HUMAN_AGENT_APPROVED = false;

export type DmSendOrigin = 'agent' | 'automated';

export const DM_STANDARD_WINDOW_HOURS = 24;
export const DM_HUMAN_AGENT_WINDOW_HOURS = 7 * 24;
/** The composer shows a countdown once this little of the window is left. */
export const DM_WINDOW_COUNTDOWN_HOURS = 24;

const HOUR_MS = 60 * 60 * 1000;

// `humanAgentApproved` exists so a test can exercise the approved branch; every
// production caller takes the default, i.e. the constant above.

/** Whether a send on this channel, with this provenance, carries HUMAN_AGENT. */
export function humanAgentTagApplies(
  channel: InboxChannel,
  origin: DmSendOrigin,
  humanAgentApproved: boolean = HUMAN_AGENT_APPROVED,
): boolean {
  return humanAgentApproved && channel === 'instagram' && origin === 'agent';
}

/** Hours after the customer's last message that a reply is allowed; null = no known window. */
export function dmReplyWindowHours(
  channel: InboxChannel,
  origin: DmSendOrigin,
  humanAgentApproved: boolean = HUMAN_AGENT_APPROVED,
): number | null {
  if (channel === 'instagram') {
    return humanAgentTagApplies(channel, origin, humanAgentApproved)
      ? DM_HUMAN_AGENT_WINDOW_HOURS
      : DM_STANDARD_WINDOW_HOURS;
  }
  if (channel === 'facebook') return DM_STANDARD_WINDOW_HOURS;
  return null;
}

/**
 * Whether the composer BLOCKS a send once the window has passed, or only warns.
 *
 * Facebook blocks (unchanged). Instagram NEVER blocks, independent of
 * HUMAN_AGENT_APPROVED: an unanswered message request can still be answered
 * past any window (24h unapproved, 7 days approved) and we cannot tell those
 * threads apart, so blocking would stop sends that would succeed. Instagram
 * WARNS instead, and the server's refusal sentence is the safety net.
 */
export function dmWindowBlocksSend(channel: InboxChannel): boolean {
  return channel === 'facebook';
}

/**
 * The customer's last inbound message time. The stored `lastInboundAt` wins;
 * a legacy thread (pre-2026-09-24, no field) falls back to the newest incoming
 * message we hold. ⚠️ NEVER `lastActivityAt` -- our own sends bump that, so
 * gating on it would keep every thread we replied to open forever.
 */
export function resolveLastInboundAt(
  stored: string | null | undefined,
  messages: readonly Pick<InboxMessage, 'direction' | 'createdAt'>[],
): string | null {
  let best: string | null = stored ?? null;
  for (const m of messages) {
    if (m.direction !== 'incoming' || !m.createdAt) continue;
    if (best === null || m.createdAt > best) best = m.createdAt;
  }
  return best;
}

export type DmReplyWindowState =
  /** ⚠️ No data, or no known window: ALLOW the send and let the platform decide. */
  | { kind: 'unknown' }
  | { kind: 'open'; closesAtMs: number; msRemaining: number; closingSoon: boolean }
  | { kind: 'closed'; closedAtMs: number };

/**
 * ⚠️ FAILS OPEN. Missing or unparseable data is 'unknown', which the composer
 * treats as sendable -- blocking a send because WE lack data is absence read as
 * a fault. The server's window-specific error is the safety net behind it.
 * 'closed' means the window has passed; whether that BLOCKS is
 * dmWindowBlocksSend's decision, not this one's.
 */
export function dmReplyWindowState(
  channel: InboxChannel,
  lastInboundAt: string | null,
  nowMs: number,
  // Required, like everywhere else the tag is decided: no default provenance.
  origin: DmSendOrigin,
  humanAgentApproved: boolean = HUMAN_AGENT_APPROVED,
): DmReplyWindowState {
  const hours = dmReplyWindowHours(channel, origin, humanAgentApproved);
  if (hours === null || !lastInboundAt) return { kind: 'unknown' };
  const inboundMs = Date.parse(lastInboundAt);
  if (!Number.isFinite(inboundMs)) return { kind: 'unknown' };
  const closesAtMs = inboundMs + hours * HOUR_MS;
  const msRemaining = closesAtMs - nowMs;
  if (msRemaining <= 0) return { kind: 'closed', closedAtMs: closesAtMs };
  return { kind: 'open', closesAtMs, msRemaining, closingSoon: msRemaining <= DM_WINDOW_COUNTDOWN_HOURS * HOUR_MS };
}

/** "5h 12m", "38m", "under a minute". */
export function formatWindowRemaining(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return 'under a minute';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function platformName(channel: InboxChannel): string {
  if (channel === 'instagram') return 'Instagram';
  if (channel === 'facebook') return 'Facebook';
  return 'The platform';
}

/** The span in words, DERIVED from dmReplyWindowHours so copy and maths cannot disagree. */
function spanPhrase(hours: number | null): string {
  if (hours === null) return 'a limited time';
  return hours % 24 === 0 && hours > 24 ? `${hours / 24} days` : `${hours} hours`;
}

// ⚠️ Every sentence below is ASCII only: operator-visible strings reach logs
// and terminals that mangle an em-dash into "?". The human composer is the
// only DM send path, so the copy is phrased for origin 'agent'.

/**
 * The sentence for a passed window -- the composer's notice when the window
 * BLOCKS, and the server's refusal for Meta's outside-window error on every
 * channel, so the operator meets one explanation.
 */
export function dmWindowClosedMessage(
  channel: InboxChannel,
  humanAgentApproved: boolean = HUMAN_AGENT_APPROVED,
): string {
  const platform = platformName(channel);
  const span = spanPhrase(dmReplyWindowHours(channel, 'agent', humanAgentApproved));
  if (channel === 'instagram' && !humanAgentApproved) {
    // ⚠️ No 7-day window, no tag: neither is in play while unapproved.
    return (
      `Instagram refused this reply because more than ${span} have passed since the customer's last message. ` +
      'The customer needs to message the club again before you can reply.'
    );
  }
  return (
    `${platform} only allows replies within ${span} of the customer's last message. ` +
    'This conversation is older than that - reply by another channel, or wait for them to message again.'
  );
}

/**
 * The composer's notice when the window has passed but does NOT block
 * (Instagram while the tag is unapproved): send stays enabled, and the
 * server's refusal sentence takes over if Instagram says no.
 */
export function dmWindowPassedWarning(
  channel: InboxChannel,
  humanAgentApproved: boolean = HUMAN_AGENT_APPROVED,
): string {
  const platform = platformName(channel);
  const span = spanPhrase(dmReplyWindowHours(channel, 'agent', humanAgentApproved));
  return (
    `It's been more than ${span} since this customer's last message. ` +
    `${platform} usually blocks replies after that unless the conversation is an unanswered message request - ` +
    `you can try, and we'll tell you if ${platform} refuses.`
  );
}

/** The countdown line inside the last DM_WINDOW_COUNTDOWN_HOURS of the window. */
export function dmWindowCountdownMessage(channel: InboxChannel, msRemaining: number): string {
  const platform = platformName(channel);
  const after = dmWindowBlocksSend(channel)
    ? `${platform} will not accept a message on this conversation.`
    : `${platform} will usually refuse a reply on this conversation.`;
  return `${formatWindowRemaining(msRemaining)} left to reply - after that ${after}`;
}
